import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

import { PuSubscribable } from '@app/utils/pu-subscribe';
import { AreaPageElementQuestionModel } from '@main-application/shared/template-editor-dialog/models/template-editor-model';
import { EColorPalette } from '@shared/enums/color-palette.enum';
import { EIcon } from '@shared/enums/icon.enum';
import { ColorPickerModule } from '@ui-components/components/color-picker/color-picker.module';
import { IconComponent } from '@ui-components/components/icon/icon.component';
import { InputModule } from '@ui-components/controls/input/input.module';
import { DialogResult } from '@ui-components/modals/config/dialog-result.enum';

@Component({
  selector: 'app-dropdown-options-editor-modal',
  templateUrl: './dropdown-options-editor-modal.component.html',
  styleUrls: ['./dropdown-options-editor-modal.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    IconComponent,
    InputModule,
    ColorPickerModule,
  ],
})
export class DropdownOptionsEditorModalComponent extends PuSubscribable implements OnInit, AfterViewInit {
  EIcon = EIcon;
  EColorPalette = EColorPalette;
  form: FormGroup;

  actionItemMenuOpen = false;
  _isColorSchemeEnabled = false;

  expandedGroups: Record<number, boolean> = {};

  get title(): FormControl {
    return this.form.get('title') as FormControl;
  }

  get options(): FormArray {
    return (this.form?.get('options') as FormArray) ?? this.fb.array([]);
  }

  get isColorSchemeEnabled(): boolean {
    return this._isColorSchemeEnabled;
  }

  @ViewChildren('inputElement') inputElements!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('item') item!: QueryList<ElementRef>;
  @ViewChildren('group') group!: QueryList<ElementRef>;

  constructor(
    public dialogRef: MatDialogRef<DropdownOptionsEditorModalComponent, { result: DialogResult; data: any }>,
    @Inject(MAT_DIALOG_DATA) public data: AreaPageElementQuestionModel,
    private fb: FormBuilder
  ) {
    super();
  }

  ngOnInit(): void {
    const grouped = new Map<string | null, any[]>();

    if (Array.isArray(this.data?.options)) {
      for (const opt of this.data.options) {
        const group = opt.group ?? null;
        if (!grouped.has(group)) grouped.set(group, []);
        grouped.get(group)?.push(opt);
      }
    }

    const optionsArray: FormGroup[] = [];

    if (Array.isArray(this.data?.options)) {
      const grouped = new Map<string, any[]>();

      for (const opt of this.data.options) {
        const key = opt.group ?? `__ungrouped__${crypto.randomUUID()}`;
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key)!.push(opt);
      }

      grouped.forEach((groupItems, key) => {
        if (!key.startsWith('__ungrouped__')) {
          const groupArray = this.fb.array(
            groupItems.map(item =>
              this.fb.group({
                label: [item.label ?? '', Validators.required],
                value: [item.value ?? crypto.randomUUID()],
                color: [item.color ?? null],
              })
            )
          );

          optionsArray.push(
            this.fb.group({
              label: [key, Validators.required],
              color: [groupItems[0]?.groupColor ?? null],
              group: groupArray,
            })
          );
        } else {
          groupItems.forEach(item => {
            optionsArray.push(
              this.fb.group({
                label: [item.label ?? '', Validators.required],
                value: [item.value ?? crypto.randomUUID()],
                color: [item.color ?? null],
              })
            );
          });
        }
      });
    }

    this._isColorSchemeEnabled = Array.isArray(this.data?.options) && this.data.options.some(o => o?.color);

    this.form = this.fb.group({
      title: this.data?.title,
      options: this.fb.array(optionsArray),
      defaultValue: new FormControl(this.data?.defaultValue ?? ''),
    });

    this.dialogRef
      .backdropClick()
      .subscribe(() => this.save())
      .untilDestroyed(this);
  }

  ngAfterViewInit(): void {
    this.inputElements.forEach(el => {
      this.updateWidthFromInput(el.nativeElement);
    });
  }

  toggleColorScheme(): void {
    this._isColorSchemeEnabled = !this._isColorSchemeEnabled;
  }

  addOptionGroup(): void {
    const group = `Item Group (${this.options?.length + 1})`;
    const groupItems = this.fb.array([
      this.fb.group({
        label: [`Item (1)`, Validators.required],
        value: [crypto.randomUUID()],
        color: [null],
      }),
    ]);

    this.options.push(
      this.fb.group({
        label: [group, Validators.required],
        color: [null],
        group: groupItems,
      })
    );

    setTimeout(() => {
      this.inputElements?.forEach(el => this.updateWidthFromInput(el.nativeElement));

      const last = this.item.last;
      if (last) {
        last.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  addOption(groupIndex?: number): void {
    if (typeof groupIndex === 'number') {
      const group = this.options.at(groupIndex);
      const groupArray = group?.get('group') as FormArray;

      groupArray.push(
        this.fb.group({
          label: [`Item (${groupArray?.length + 1})`, Validators.required],
          value: [crypto.randomUUID()],
          color: [null],
        })
      );
    } else {
      this.options.push(
        this.fb.group({
          label: [`Item (${this.options?.length + 1})`, Validators.required],
          value: [crypto.randomUUID()],
          color: [null],
        })
      );
    }

    setTimeout(() => {
      this.inputElements?.forEach(el => this.updateWidthFromInput(el.nativeElement));

      if (typeof groupIndex === 'number') {
        const groupItems = this.group
          .toArray()
          .filter(el => el.nativeElement.closest(`[formGroupName="${groupIndex}"]`));
        const last = groupItems[groupItems.length - 1];
        if (last) {
          last.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      } else {
        const last = this.item.last;
        if (last) {
          last.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });
  }

  removeOption(index: number): void {
    this.options.removeAt(index);
  }

  removeGroupItem(groupIndex: number, itemIndex: number): void {
    const group = this.options.at(groupIndex)?.get('group') as FormArray;
    group.removeAt(itemIndex);
  }

  setDefault(groupIndex: number, itemIndex?: number): void {
    let value: string;

    if (typeof itemIndex === 'number') {
      value = (this.options.at(groupIndex).get('group') as FormArray).at(itemIndex).get('value')?.value;
    } else {
      value = this.options.at(groupIndex).get('value')?.value;
    }

    const currentDefault = this.form.get('defaultValue')?.value;

    currentDefault === value
      ? this.form.get('defaultValue')?.setValue(null)
      : this.form.get('defaultValue')?.setValue(value);
  }

  isDefault(groupIndex: number, itemIndex?: number): boolean {
    const selected = this.form.get('defaultValue')?.value;

    const current =
      typeof itemIndex === 'number'
        ? (this.options.at(groupIndex).get('group') as FormArray).at(itemIndex).get('value')?.value
        : this.options.at(groupIndex).get('value')?.value;

    return selected === current;
  }

  setExpanded(index: number): void {
    const current = this.expandedGroups[index];
    this.expandedGroups[index] = current === undefined ? false : !current;

    setTimeout(() => {
      this.inputElements?.forEach(el => {
        this.updateWidthFromInput(el.nativeElement);
      });
    });
  }

  isExpanded(index: number): boolean {
    return this.expandedGroups[index] !== false;
  }

  save(): void {
    const flatOptions: any[] = [];

    this.options.controls.forEach(option => {
      const label = option.get('label')?.value || '';
      const color = option.get('color')?.value || null;

      if (option.get('group') instanceof FormArray) {
        const groupArray = option.get('group') as FormArray;

        const groupItems = groupArray.controls.map(groupItem => ({
          label: groupItem.get('label')?.value,
          value: groupItem.get('value')?.value,
          color: groupItem.get('color')?.value,
          group: label,
        }));

        flatOptions.push(...groupItems);
      } else {
        flatOptions.push({
          label,
          value: option.get('value')?.value,
          color,
          group: null,
        });
      }
    });

    const sorted = flatOptions.sort(
      (a, b) =>
        (a.group ?? '').localeCompare(b.group ?? '', undefined, { sensitivity: 'base' }) ||
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
    );

    const result: AreaPageElementQuestionModel = {
      ...this.data,
      title: this.title.value,
      defaultValue: this.form.get('defaultValue')?.value,
      options: sorted,
    };

    this.dialogRef.close({ result: DialogResult.Success, data: result });
  }

  copyOptionsToClipboard(): void {
    const lines: string[] = [];

    this.options.controls.forEach(option => {
      const label = option.get('label')?.value?.trim() || '';
      const color = option.get('color')?.value?.trim();
      const colorSuffix = color ? ` #${color.replace(/^#/, '')}` : '';

      if (option.get('group') instanceof FormArray) {
        lines.push(`${label}${colorSuffix}`);
        const groupItems = option.get('group') as FormArray;

        groupItems.controls.forEach(groupItem => {
          const subLabel = groupItem.get('label')?.value?.trim() || '';
          const subColor = groupItem.get('color')?.value?.trim();
          const subColorSuffix = subColor ? ` #${subColor.replace(/^#/, '')}` : '';
          lines.push(`\t${subLabel}${subColorSuffix}`);
        });
      } else {
        lines.push(`${label}${colorSuffix}`);
      }
    });

    navigator.clipboard.writeText(lines.join('\n')).catch(err => console.error('Clipboard write failed', err));
  }

  pasteOptionsFromClipboard(): void {
    if (!navigator.clipboard?.readText) {
      console.warn('Clipboard API not available.');
      return;
    }

    navigator.clipboard
      .readText()
      .then(text => {
        const lines = text.split('\n').filter(Boolean);
        if (!lines?.length) return;

        const parsed: any[] = [];
        let currentGroup: { label: string; color: string | null; group: any[] } | null = null;

        for (const line of lines) {
          const isGroupItem = line.startsWith('\t');
          const raw = isGroupItem ? line.slice(1).trim() : line.trim();
          const match = raw.match(/^(.+?)(?:\s+#([A-Fa-f0-9]{6}))?$/);

          if (!match) {
            console.warn('Invalid format line:', line);
            return;
          }

          const [_, label, color] = match;
          const fullColor = color ? `#${color}` : null;

          if (isGroupItem) {
            if (!currentGroup) {
              console.warn('Group item without a preceding group:', line);
              return;
            }
            currentGroup.group.push({
              label,
              value: crypto.randomUUID(),
              color: fullColor,
            });
          } else {
            if (currentGroup) parsed.push(currentGroup);
            currentGroup = {
              label,
              color: fullColor,
              group: [],
            };
          }
        }

        if (currentGroup) parsed.push(currentGroup);

        parsed.forEach(entry => {
          if (entry.group?.length > 0) {
            const groupArray = this.fb.array(
              entry.group.map(item =>
                this.fb.group({
                  label: [item.label, Validators.required],
                  value: [item.value],
                  color: [item.color],
                })
              )
            );

            this.options.push(
              this.fb.group({
                label: [entry.label, Validators.required],
                color: [entry.color],
                group: groupArray,
              })
            );
          } else {
            this.options.push(
              this.fb.group({
                label: [entry.label, Validators.required],
                value: [crypto.randomUUID()],
                color: [entry.color],
              })
            );
          }
        });

        setTimeout(() => {
          const lastInput = this.inputElements.get(this.inputElements?.length - 1);
          if (lastInput) this.updateWidthFromInput(lastInput.nativeElement);
        });
      })
      .catch(err => {
        console.error('Clipboard read failed:', err);
      });
  }

  cancel(): void {
    this.save();
  }

  public updateWidthFromInput(input: HTMLInputElement): void {
    let value = input.value;
    value = value === '?' ? '' : value;
    if (value !== input.value) {
      input.value = value;
      input.selectionStart = input.selectionEnd = value?.length - 1;
    }

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;

    const style = getComputedStyle(input);
    context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const textWidth = context.measureText(value || input.placeholder).width;

    const buffer = style.fontWeight === '600' ? 2 : 0;
    input.style.width = `${Math.ceil(textWidth + buffer)}px`;
  }

  focusNextInput(e: Event, i?: number, isGroupItem = false): void {
    const currentInput = e?.target as HTMLInputElement;

    const allInputs: HTMLInputElement[] = Array.from(document.querySelectorAll('input.input'));

    const currentIndex = allInputs.indexOf(currentInput);
    const nextInput = allInputs[currentIndex + 1];

    if (nextInput) {
      nextInput.focus();

      const length = nextInput.value.length;
      nextInput.setSelectionRange(length, length);
    } else {
      if (isGroupItem && typeof i === 'number') {
        this.addOption(i);
      } else {
        this.addOption();
      }

      setTimeout(() => {
        const allInputsAfter: HTMLInputElement[] = Array.from(document.querySelectorAll('input.input'));
        const last = allInputsAfter[allInputsAfter.length - 1];
        last?.focus();
        if (last) {
          const length = last.value.length;
          last.setSelectionRange(length, length);
        }
      });
    }
  }
}
